import { useCallback } from "react";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";
import type { ExtraBlock } from "@/data/siteContent";

interface AppendableBlocksProps {
  blocks: ExtraBlock[];
  onChange: (blocks: ExtraBlock[]) => void;
  className?: string;
}

export default function AppendableBlocks({ blocks, onChange, className = "" }: AppendableBlocksProps) {
  const { isEditing: isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();

  const textColor = isDark ? "text-gray-300" : "text-gray-600";
  const headingColor = isDark ? "text-white" : "text-gray-900";

  const updateBlock = useCallback((id: string, content: string) => {
    onChange(blocks.map((b) => (b.id === id ? { ...b, content } : b)));
  }, [blocks, onChange]);

  const removeBlock = useCallback((id: string) => {
    onChange(blocks.filter((b) => b.id !== id));
  }, [blocks, onChange]);

  const addBlock = useCallback((type: ExtraBlock["type"]) => {
    const newBlock: ExtraBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type,
      content:
        type === "heading"
          ? "New Heading"
          : "Click to edit this text block. You can add multiple paragraphs by pressing Enter.",
    };
    onChange([...blocks, newBlock]);
  }, [blocks, onChange]);

  const moveBlock = useCallback((id: string, direction: -1 | 1) => {
    const idx = blocks.findIndex((b) => b.id === id);
    if (idx === -1) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= blocks.length) return;
    const newBlocks = [...blocks];
    [newBlocks[idx], newBlocks[newIdx]] = [newBlocks[newIdx], newBlocks[idx]];
    onChange(newBlocks);
  }, [blocks, onChange]);

  if (!isAdmin && blocks.length === 0) return null;

  return (
    <div className={`space-y-6 ${className}`}>
      {blocks.map((block, index) => (
        <div key={block.id} className="relative group/block">
          {isAdmin && (
            <div className="absolute -top-2 -right-2 z-20 flex gap-1 opacity-0 group-hover/block:opacity-100 transition-opacity">
              {index > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveBlock(block.id, -1);
                  }}
                  className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 cursor-pointer transition-colors"
                  title="Move up"
                >
                  <i className="ri-arrow-up-line text-xs"></i>
                </button>
              )}
              {index < blocks.length - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    moveBlock(block.id, 1);
                  }}
                  className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 cursor-pointer transition-colors"
                  title="Move down"
                >
                  <i className="ri-arrow-down-line text-xs"></i>
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeBlock(block.id);
                }}
                className="w-6 h-6 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-500 text-red-400 hover:text-white cursor-pointer transition-colors"
                title="Delete block"
              >
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            </div>
          )}

          {block.type === "heading" ? (
            isAdmin ? (
              <AdminEditField
                value={block.content}
                onSave={(v) => updateBlock(block.id, v)}
                onDelete={() => removeBlock(block.id)}
                className="relative"
                inputClassName="text-xl font-bold"
              >
                <h4
                  className={`text-xl md:text-2xl font-bold ${headingColor}`}
                  style={{ fontFamily: headingFont, ...headingStyle }}
                >
                  {block.content}
                </h4>
              </AdminEditField>
            ) : (
              <h4
                className={`text-xl md:text-2xl font-bold ${headingColor}`}
                style={{ fontFamily: headingFont, ...headingStyle }}
              >
                {block.content}
              </h4>
            )
          ) : isAdmin ? (
            <AdminEditField
              value={block.content}
              onSave={(v) => updateBlock(block.id, v)}
              onDelete={() => removeBlock(block.id)}
              multiline
              className="relative"
              inputClassName="text-base"
            >
              <p className={`text-base leading-relaxed ${textColor}`} style={{ whiteSpace: "pre-line" }}>
                {block.content}
              </p>
            </AdminEditField>
          ) : (
            <p className={`text-base leading-relaxed ${textColor}`} style={{ whiteSpace: "pre-line" }}>
              {block.content}
            </p>
          )}
        </div>
      ))}

      {isAdmin && (
        <div className="flex gap-3 pt-2">
          <button
            onClick={() => addBlock("text")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-dashed border-pink-300 text-pink-500 hover:text-pink-700 hover:border-pink-400 hover:bg-pink-50/50 text-xs font-semibold cursor-pointer transition-colors"
          >
            <i className="ri-add-line"></i>
            Add Text Block
          </button>
          <button
            onClick={() => addBlock("heading")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-dashed border-green-300 text-green-600 hover:text-green-800 hover:border-green-400 hover:bg-green-50/50 text-xs font-semibold cursor-pointer transition-colors"
          >
            <i className="ri-add-line"></i>
            Add Heading Block
          </button>
        </div>
      )}
    </div>
  );
}